/**
 * THE single safe wrapper for Web Storage (BLOCK S12, Item 1).
 *
 * Reading window.localStorage / window.sessionStorage THROWS a SecurityError
 * ("Access is denied for this document") when a browser blocks third-party
 * storage — e.g. /embed/lead-form embedded in an iframe on a client's site.
 * An unguarded read in the App root's auth-restore effect took the WHOLE app
 * blank on every route. This module never throws: it probes real storage once,
 * and if it is unavailable (or any call later fails) it transparently falls
 * back to an in-memory store for the life of the page.
 *
 * It returns a Proxy that mimics the Storage API exactly, so it is a drop-in
 * for every call form used in the codebase:
 *   appStorage.getItem('k') / setItem / removeItem / clear / key / length
 *   appStorage.token            (property access)
 *   appStorage['token']         (bracket access)
 *
 * Behaviour with real storage is identical to before: same keys, same values.
 */

const STORAGE_METHODS = ['getItem', 'setItem', 'removeItem', 'clear', 'key'];

function makeSafeStorage(kind) {
  const mem = new Map();

  let real = null;
  try {
    const candidate = kind === 'session' ? window.sessionStorage : window.localStorage;
    const probe = '__safe_storage_probe__';
    candidate.setItem(probe, '1');
    candidate.removeItem(probe);
    real = candidate;
  } catch (_e) {
    real = null; // storage blocked -> memory-only for this page
  }

  const memApi = {
    getItem: (k) => (mem.has(String(k)) ? mem.get(String(k)) : null),
    setItem: (k, v) => { mem.set(String(k), String(v)); },
    removeItem: (k) => { mem.delete(String(k)); },
    clear: () => { mem.clear(); },
    key: (i) => {
      const keys = Array.from(mem.keys());
      return i >= 0 && i < keys.length ? keys[i] : null;
    },
  };

  // Run against real storage when possible; on ANY failure fall back to memory
  // so a mid-session revocation can never throw.
  const call = (method, args) => {
    if (real) {
      try {
        return real[method].apply(real, args);
      } catch (_e) {
        /* fall through to memory */
      }
    }
    return memApi[method].apply(memApi, args);
  };

  const readLength = () => {
    if (real) {
      try {
        return real.length;
      } catch (_e) {
        /* fall through */
      }
    }
    return mem.size;
  };

  const handler = {
    get(_target, prop) {
      if (STORAGE_METHODS.indexOf(prop) !== -1) {
        return (...args) => call(prop, args);
      }
      if (prop === 'length') {
        return readLength();
      }
      if (prop === Symbol.toPrimitive || typeof prop === 'symbol') {
        return undefined;
      }
      // Arbitrary property access, e.g. appStorage.token -> native returns
      // undefined when absent (getItem returns null), so mirror native here.
      const v = call('getItem', [prop]);
      return v == null ? undefined : v;
    },
    set(_target, prop, value) {
      if (typeof prop === 'string') {
        call('setItem', [prop, value]);
      }
      return true;
    },
    deleteProperty(_target, prop) {
      if (typeof prop === 'string') {
        call('removeItem', [prop]);
      }
      return true;
    },
    has(_target, prop) {
      if (STORAGE_METHODS.indexOf(prop) !== -1 || prop === 'length') return true;
      return call('getItem', [prop]) != null;
    },
  };

  return new Proxy(memApi, handler);
}

export const appStorage = makeSafeStorage('local');
export const appSession = makeSafeStorage('session');
